import { useEffect, useMemo, useReducer } from 'react'
import MyTabsIndex from './MyTabIndex'
import {
  AIModelTabEnum,
  getTabs_aiArtworkResourceModule_aiSquareTab,
  getTabs_aiArtworkResourceModule_aiSquareTab_tab_child,
} from '~/graphqls/types'
import { AutoScroll } from '~/components/ui/AutoScroll'
import classNames from 'classnames'
import TagSelect from './TagSelect'
import { useRouter } from 'next/router'
import AuthorCard from './AuthorCard'
import { MyBtn } from '~/components/ui/MyBtn'
import useScrollTop from '~/hooks/useScrollTop'
import SimpleSearch from './SimpleSearch'
import { genMapFromTabs, getSearchParams } from '~/utils/search'
import { observer } from 'mobx-react-lite'
import { useAuth } from '~/hooks/useAuth'

import styles from './Tabs.module.css'
import useSearchParams from '~/hooks/useSearchParam'
import TabArtistTiled from './TabArtistTiled'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { MyIcon } from '~/components/ui/MyIcon'
import { useValidateAuth } from '~/hooks/useValidateAuth'

interface Props {
  /** 工作流 */
  isWorkflow?: boolean
  tabs: getTabs_aiArtworkResourceModule_aiSquareTab
}

function Tabs({ isWorkflow = false, tabs }: Props) {
  const { validateIsLogin } = useValidateAuth()
  const { isLogin } = useAuth()
  const router = useRouter()
  const scrollTop = useScrollTop()
  const isTabSticky = scrollTop > 320
  const changeRoute = (obj: Record<string, number | string>) => {
    const pathname = isWorkflow ? '/' : router?.pathname
    const query = isWorkflow ? null : router.query
    router.replace(
      {
        pathname: pathname,
        query: {
          ...query,
          ...obj,
        },
      },
      undefined,
      { shallow: true, scroll: isTabSticky }
    )
  }
  // 同步url query中的数据
  const { artistid, themeid, l_1, l_2, activeLeaf } = useSearchParams()
  const changeTheme = (id: string) => {
    if (id !== themeid) {
      changeRoute({ themeid: id, sort: '最新' }) // 备注：老板要求选中主题就按最新排序
    } else {
      changeRoute({ themeid: '', sort: '推荐' })
    }
  }
  const changeL_1 = (name: string) => {
    if (name === '工作流') {
      router.replace({
        pathname: 'workflow-plaza',
        query: {
          l_1: name,
        },
      })
      return
    }
    changeRoute({ l_1: name, l_2: 0, themeid: '', artistid: '', modeltype: '' })
  }
  const changeL_2 = (index: number) => changeRoute({ l_2: index })
  const setActiveLeaf = (arr: string[]) => {
    const n = arr.join('.')
    const old = activeLeaf.join('.')
    if (n !== old) {
      changeRoute({ leaf: arr.join('.') })
    }
  }

  useDidUpdateEffect(() => {
    setActiveLeaf([])
  }, [l_1])

  /** tagid => tag 的 map */
  const idMap = useMemo(() => {
    return genMapFromTabs(tabs)
  }, [tabs])

  /** 根据数据生成的tab */
  const tab1Options = useMemo(() => {
    if (tabs.tab?.child) {
      const ret = tabs.tab?.child.map(item => {
        return {
          ...item,
          name: item?.name || '',
          id: item?.idStr || '',
          child: item?.child || [],
        }
      })
      // 工作流是独立的模块
      ret.push({
        name: '工作流',
        id: -2,
        child: [],
        isNew: true,
      } as any)
      if (isLogin) {
        ret.splice(0, 0, {
          name: '关注',
          id: -1,
          child: [],
        } as any)
      }
      return ret
    }
    return []
  }, [tabs])
  const l_1_index = useMemo(() => {
    let index = tab1Options.map(item => item.name).indexOf(l_1)
    return Math.max(index, 0)
  }, [tab1Options, l_1])
  const tab2Options = useMemo(() => {
    return (
      tab1Options[l_1_index]?.child.map(item => {
        return {
          ...item,
          name: item?.name || '',
          id: item?.idStr || '',
          child: item?.child || [],
          count: 0,
        }
      }) || []
    )
  }, [l_1_index, tab1Options])
  const tab3Options = useMemo(() => {
    const temp =
      tab2Options[l_2]?.child.map(item => {
        return {
          ...item,
          name: item?.name || '',
          id: item?.idStr || '',
          child: (item?.child || []).map(i => {
            return {
              ...i,
              id: i.idStr,
            }
          }),
        }
      }) || []
    if (l_1 === '全部' && l_2 === 0) {
      temp.splice(1, 0, {
        idStr: '-1',
        name: '合集',
        icon: 'https://data.wujiebantu.com/FoKziSLuFXVaa901tVgXTGKzK1As.png',
        child: [],
      } as any)
    }
    return temp
  }, [l_2, tab2Options, l_1])
  const curTab2 = tab2Options[l_2]

  /** 主题tag */
  let themeTag = []
  let themeId2Index: Record<string, number> = {}
  if (curTab2?.tabType === AIModelTabEnum.SQUARE_CREATE_TAG) {
    for (let i = 0; i < tab3Options.length; ) {
      const a = tab3Options[i++]
      const b = tab3Options[i++]
      if (b) {
        themeTag.push([a, b])
        themeId2Index[a.id] = Math.floor((i - 1) / 2)
        themeId2Index[b.id] = Math.floor((i - 1) / 2)
      } else {
        themeTag.push([a])
        themeId2Index[a.id] = Math.floor((i - 1) / 2)
      }
    }
  }

  const isArtistTag = curTab2?.tabType === AIModelTabEnum.ARTIST_TAG
  const isThemeTag = curTab2?.tabType === AIModelTabEnum.SQUARE_CREATE_TAG
  const tab3OptionsIsLeaf = tab3Options?.[0]?.isLeaf
  const curArtist = idMap.get(artistid || '-1')

  const toggleLeaf = (id: string) => {
    let change = 0
    if (activeLeaf.indexOf(id) !== -1) {
      change = -1
      setActiveLeaf(activeLeaf.filter(item => item !== id))
    } else {
      change = 1
      setActiveLeaf([...activeLeaf, id])
    }
  }

  const clearTagUnderL2 = () => {
    changeRoute({ themeid: '', artistid: '', leaf: '' })
  }
  const showClearBtn = themeid || activeLeaf.length !== 0 || artistid
  // 根据 activeLeaf 更新标签选中的计数, (二级标签上有选中的子标签计数)
  const [, forceUpdate] = useReducer(() => ({}), {})
  useEffect(() => {
    let tab2OptionIDs: string[] = []
    tab2Options.forEach(item => {
      item.count = 0
      tab2OptionIDs.push(item.idStr!)
    })
    activeLeaf.forEach(id => {
      const item = idMap.get(id)
      // 遍历父节点，设置二级tab激活的子节点数目(不包含主题，和艺术家，主题和艺术家另算)
      const recursive = (pid?: string | null | undefined) => {
        if (!pid) return
        const cur = idMap.get(pid)
        if (!cur) return
        let index = tab2OptionIDs.indexOf(cur.idStr!)
        if (index !== -1) {
          tab2Options[index].count += 1
          return
        }
        recursive(cur.pid)
      }
      recursive(item?.pid)
    })
    forceUpdate()
  }, [activeLeaf.length])

  const queryParams = useMemo(() => {
    return getSearchParams(router.query, tabs)
  }, [router.query, tabs])

  return (
    <>
      <div
        className={classNames(
          'flex w-full bg-neutral-100 z-[998] pt-4 pb-3 items-center sticky top-[40px]'
        )}
      >
        <MyTabsIndex
          className="text-base"
          activeIndex={l_1_index}
          onChange={index => {
            changeL_1(tab1Options[index].name)
          }}
          items={tab1Options}
        />

        {!isTabSticky && (
          <>
            {tab2Options?.length !== 0 && (
              <MyTabsIndex
                className="text-sm absolute bottom-0 left-1/2 -translate-x-1/2 -translate-y-[10px]"
                hiddenUnderline
                activeIndex={l_2}
                onChange={changeL_2}
                items={tab2Options}
                showCount
              ></MyTabsIndex>
            )}

            <div className="absolute right-0 -mt-1">
              {showClearBtn && (
                <MyBtn
                  $dark
                  className="!h-8 !py-1 !px-4"
                  onClick={clearTagUnderL2}
                >
                  清空
                </MyBtn>
              )}

              <MyBtn
                type="primary"
                className="!bg-[#F9DEB9] !border-[#F9DEB9] !text-neutral-50 ml-3 !h-8 !py-1 !px-4"
                onClick={() => {
                  router.push('/rank')
                }}
              >
                <MyIcon icon="paihangbang" className="mr-2 align-text-top" />
                排行榜
              </MyBtn>

              <MyBtn
                className="ml-3 !h-8 !py-1 !px-4"
                type="primary"
                onClick={() => {
                  if (validateIsLogin()) {
                    router.push('/ai/community/select-ai')
                  }
                }}
              >
                <MyIcon icon="fabuchuangzuo" className="mr-2 align-text-top" />
                发布 AI 创作
              </MyBtn>
            </div>
          </>
        )}

        {isTabSticky && (
          <div className="absolute right-0">
            <SimpleSearch />
          </div>
        )}
      </div>

      {/* 主题标签 */}
      {isThemeTag && (
        <AutoScroll
          key={l_1_index}
          activeIndex={themeId2Index[themeid as string]}
          hasArrow={themeTag.length > 8}
        >
          <div className="flex">
            {themeTag.map((item, index) => {
              return (
                <div
                  key={index}
                  className={classNames({
                    'ml-3': index > 0,
                  })}
                >
                  <div
                    className={classNames(styles.theme, {
                      [styles.themeActive]: themeid === item[0].idStr,
                    })}
                    style={{
                      backgroundImage: `linear-gradient(
                        to bottom,
                        rgba(0, 0, 0, 0.1),
                        rgba(0, 0, 0, 0.5)
                      ), url(${item[0].icon})`,
                    }}
                    onClick={() => {
                      changeTheme(item[0].idStr!)
                    }}
                  >
                    <div className={styles.themeHover}>{item[0].name}</div>
                  </div>
                  {!!item[1] && (
                    <div
                      className={classNames(styles.theme, 'mt-2', {
                        [styles.themeActive]: themeid === item[1].idStr,
                      })}
                      style={{
                        backgroundImage: `linear-gradient(
                          to bottom,
                          rgba(0, 0, 0, 0.1),
                          rgba(0, 0, 0, 0.5)
                        ), url(${item[1].icon})`,
                      }}
                      onClick={() => {
                        changeTheme(item[1].idStr!)
                      }}
                    >
                      <div className={styles.themeHover}>{item[1].name}</div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </AutoScroll>
      )}

      {/* 艺术家标签 */}
      {isArtistTag && (
        <div>
          <TabArtistTiled items={tab3Options as any} />
          {curArtist && (
            <div className="mt-4">
              <AuthorCard
                item={
                  curArtist as getTabs_aiArtworkResourceModule_aiSquareTab_tab_child
                }
              />
            </div>
          )}
        </div>
      )}

      {/* 平铺的标签 */}
      {tab3OptionsIsLeaf && !isThemeTag && !isArtistTag && (
        <TagSelect
          items={tab3Options}
          actives={activeLeaf}
          toggleLeaf={toggleLeaf}
        />
      )}

      {/* 带标题平铺的标签 */}
      {!tab3OptionsIsLeaf && !isArtistTag && (
        <div className="-mb-4">
          {tab3Options.map(item => {
            return (
              <div className="flex items-start mb-4" key={item.name}>
                <div className="w-[64px] flex-shrink-0 pt-[3px]">
                  {item.name}
                </div>
                <div className="w-full">
                  <TagSelect
                    items={item.child as any}
                    actives={activeLeaf}
                    toggleLeaf={toggleLeaf}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

export default observer(Tabs)
